import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card, Button, Modal, Spinner } from 'react-bootstrap';
import { InfoCircle, ClockHistory, GeoAlt, Newspaper, FileText, List, ArrowClockwise } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import usePersonalizedReport, { getStateDisplayName } from '../../hooks/usePersonalizedReport';
import useFeedData from '../../hooks/useFeedData';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import openCapacitorSite from '../../utils/browserUtils';
import '../feed/FeedItem.css';
import './PersonalizedNewsReport.css';

// How many crawl updates to show. useFeedData already over-fetches on top of
// this to absorb future-dated records, so there's no need to pad it here.
const UPDATES_COUNT = 5;

// Helper to process content and make citations clickable
const processCitationsInContent = (content, citations) => {
  if (!content || !citations || citations.length === 0) return content;
  
  let processed = content;
  
  citations.forEach((citation, index) => {
    const citationNumber = index + 1;
    const pattern = new RegExp(`\\[${citationNumber}\\]`, 'g');
    if (citation.url) {
      processed = processed.replace(pattern, `[[${citationNumber}]](${citation.url})`);
    }
  });
  
  return processed;
};

// Custom link renderer for ReactMarkdown to open links with Capacitor
const MarkdownLink = ({ href, children }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openCapacitorSite(href, '_blank');
  };

  return (
    <a href={href} onClick={handleClick} style={{ cursor: 'pointer' }}>
      {children}
    </a>
  );
};

// Content renderer component with clickable citations
const ReportContent = ({ content, citations, className = '' }) => {
  const processedContent = processCitationsInContent(content, citations);
  
  return (
    <div className={`report-markdown-content ${className}`}>
      <ReactMarkdown components={{ a: MarkdownLink }}>
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

// Helper to get content from report (handles old and new format)
const getReportContent = (report) => {
  if (!report) return null;
  if (report.content) return report.content;
  const parts = [report.localContent, report.stateContent].filter(Boolean);
  return parts.length > 0 ? parts.join('\n\n---\n\n') : null;
};

// Helper to get all news from report (handles old and new format)
const getReportNews = (report) => {
  if (!report) return [];
  if (report.news) return report.news;
  return [
    ...(report.localNews || []),
    ...(report.stateNews || [])
  ];
};

// Helper to get plain text preview from markdown content
const getPlainTextPreview = (content, maxLength = 100) => {
  if (!content) return '';
  const plainText = content
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return plainText.length > maxLength 
    ? plainText.substring(0, maxLength) + '...' 
    : plainText;
};

// The saved city record is the authoritative source for both the city name and
// the state it sits in; homeCityState / homeCityStateName are the older keys,
// and homeState is the user's chosen state, which is not always the city's.
const readCityData = () => {
  try {
    const raw = localStorage.getItem('homeCityData');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getCrawlCity = () => {
  const cityData = readCityData();
  return cityData?.name || localStorage.getItem('homeCity') || null;
};

const getCrawlState = (reportType, stateKey) => {
  if (reportType === 'state') {
    if (stateKey) return getStateDisplayName(stateKey);
    return (
      localStorage.getItem('homeStateName') ||
      getStateDisplayName(localStorage.getItem('homeState'))
    );
  }

  const cityData = readCityData();
  return (
    cityData?.state ||
    cityData?.province ||
    cityData?.region ||
    localStorage.getItem('homeCityStateName') ||
    localStorage.getItem('homeCityState') ||
    localStorage.getItem('homeStateName') ||
    getStateDisplayName(localStorage.getItem('homeState'))
  );
};

/**
 * Params for the crawl.govglance.org "recent updates" feed matching this
 * report's scope. The `crawl=recent` marker is what useFeedData routes on, so
 * these go through the same fetch path as the Updates feeds on the state pages.
 *
 * `level` is deliberately absent for state: it narrows results to the state
 * government's own sites, and the counties and towns are the point.
 */
const buildCrawlParams = (reportType, stateKey) => {
  if (reportType === 'federal') {
    return 'crawl=recent&country=us&level=federal&since=7d';
  }

  const state = getCrawlState(reportType, stateKey);

  if (reportType === 'state') {
    if (!state) return null;
    return `crawl=recent&country=us&state=${encodeURIComponent(state)}&since=7d`;
  }

  const city = getCrawlCity();
  // One city over one week is often nothing, so the local window is wider. With
  // no city to filter on, fall back to the surrounding state rather than
  // nothing at all.
  if (!city) {
    return state
      ? `crawl=recent&country=us&state=${encodeURIComponent(state)}&since=7d`
      : null;
  }
  return `crawl=recent&city=${encodeURIComponent(city)}${state ? `&state=${encodeURIComponent(state)}` : ''}&since=30d`;
};

// normalizeCrawlItem emits created_at (useFeedData's future-date filter reads
// it). Title and url stay tolerant because a crawled page may have neither a
// usable title nor a date, and a blank card is worse than a bare URL.
const getUpdateTitle = (item) => item.title || item.name || item.url || '';
const getUpdateUrl = (item) => item.url || item.link || null;
const getUpdateDate = (item) => item.created_at || item.date || null;

/**
 * PersonalizedNewsReport Component
 * 
 * @param {boolean} isFirstCarousel - Whether this is the first carousel (affects top margin)
 * @param {string} reportType - 'federal', 'state', or 'local'
 * @param {string} stateKey - Required for 'state' type (e.g., 'indiana', 'california')
 * @param {boolean} showOnDesktop - Opt in to rendering on desktop. Off by
 *   default so the home and state pages, which have their own desktop layouts,
 *   keep behaving exactly as they do now. LocalPage passes it.
 * @param {string} updatesMode - How the website-updates section behaves.
 *   'fallback' (default) shows updates only when no report could be generated,
 *   which is what LocalPage and the state pages want: a missing report there
 *   would otherwise leave nothing on screen.
 *   'always' shows them as their own section underneath the report.
 *   'never' drops them entirely — no crawl request, no section, no heading.
 *   Glance passes this: the ranked list already fills the page, so standing in
 *   for a missing report would only stack a second feed on top of it (and,
 *   while both were still loading, label the whole block Web Crawl Updates).
 */
const PersonalizedNewsReport = ({ 
  isFirstCarousel = false, 
  reportType = 'local',
  stateKey = null,
  showOnDesktop = false,
  updatesMode = 'fallback'
}) => {
  const { t } = useTranslation('common');
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const {
    currentReport,
    reportHistory,
    loading,
    generating,
    timeUntilNextReport,
    hasLocationData
  } = usePersonalizedReport(reportType, stateKey);

  // Website updates, used only when there is no report to show. Gated on
  // whether this instance renders at all, so a desktop page that opts out never
  // fires the request — and on updatesMode, so a caller that has its own
  // content below (Glance) doesn't request a feed it will never display.
  const renders = isMobile || showOnDesktop;
  const updatesEnabled = updatesMode !== 'never';
  const crawlParams = useMemo(
    () => ((renders && updatesEnabled) ? buildCrawlParams(reportType, stateKey) : null),
    [renders, updatesEnabled, reportType, stateKey]
  );

  // Null params are a no-op in useFeedData, so nothing is requested until the
  // scope is actually known. A place with no indexed government domain comes
  // back from the crawl service as a 400 rather than an empty result set, so
  // updatesError and an empty list mean the same thing to this component.
  const {
    data: updates = [],
    loading: updatesLoading,
    error: updatesError
  } = useFeedData(crawlParams, UPDATES_COUNT);

  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton
  } = useCollections();

  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const reportSectionRef = useRef(null);

  // The collection buttons now come from useCollections, which owns both the
  // click handler and the saved/unsaved icon. These builders only shape the
  // item; opening the modal is the button's job.
  const buildCitationCollectionItem = (citation) => ({
    title: citation.title,
    url: citation.url,
    date: citation.date,
    feedName: 'News Report',
    branch: 'News',
    topic: 'Report'
  });

  const buildUpdateCollectionItem = (item) => ({
    title: getUpdateTitle(item),
    url: getUpdateUrl(item),
    date: getUpdateDate(item),
    feedName: 'Web Crawl Updates',
    branch: 'Web Crawl Updates',
    topic: reportType
  });

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReport(null);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
  };

  // Mobile only unless the page opts in
  if (!renders) return null;

  if (reportType === 'local') {
    const homeCity = localStorage.getItem('homeCity');
    if (!homeCity) return null;
  }

  if (reportType === 'state' && !stateKey) {
    const homeState = localStorage.getItem('homeState');
    if (!homeState) return null;
  }

  const displayReport = selectedReport || currentReport;
  const content = getReportContent(displayReport);
  const allNews = getReportNews(displayReport);
  
  const currentContent = getReportContent(currentReport);
  const currentNews = getReportNews(currentReport);

  // A saved report has no url, so its identity in the collection is title plus
  // date — which is why the date here is the report's own formatted date rather
  // than a fresh timestamp wherever one exists.
  const reportCollectionItem = {
    title: displayReport?.reportTitle
      ? `${displayReport.reportTitle} Report`
      : 'News Report',
    url: null,
    date: displayReport?.formattedDate || new Date().toISOString(),
    feedName: 'Personalized News Report',
    branch: 'News',
    topic: reportType,
    type: 'report',
    content: content,
    sources: allNews
  };

  const reportTitle = t('common:report', 'Report');
  const updatesTitle = t('common:webCrawlUpdates', 'Web Crawl Updates');

  const hasReport = Boolean(currentReport && (currentContent || currentNews.length > 0));
  const visibleUpdates = updatesEnabled ? updates.slice(0, UPDATES_COUNT) : [];
  // In 'always' mode the updates are a section of the page in their own right,
  // so it renders even with nothing to show and says so. In 'fallback' mode it
  // only stands in for a report that couldn't be generated, and an empty result
  // means the section simply isn't there. In 'never' mode there is no section
  // at all and the component is the report or nothing.
  const showUpdatesSection = !updatesEnabled
    ? false
    : updatesMode === 'always'
      ? Boolean(crawlParams)
      : (!hasReport && visibleUpdates.length > 0);
  const updatesAreEmpty = !updatesLoading && visibleUpdates.length === 0;
  // The heading is committed while the section is still loading, so it has to
  // name whatever is actually pending. `hasReport` is false for a report that
  // simply hasn't arrived yet, so keying on it alone labelled a loading report
  // "Web Crawl Updates" and then flipped once it resolved. While the report is
  // in flight the report is what's coming; updates only own the heading once
  // the report has come back with nothing and they're the section still
  // loading. With updates off, the report is the only thing this can show.
  const sectionTitle = (loading || hasReport || !updatesEnabled) ? reportTitle : updatesTitle;

  const formatDate = (inputDate) => {
    if (!inputDate) return '';
    const date = new Date(inputDate);
    if (Number.isNaN(date.getTime())) return '';
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const day = utcDate.getDate();
    const month = utcDate.getMonth() + 1;
    const year = utcDate.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Still waiting on the report, or waiting on the updates that would stand in
  // for it. Either way the section has something coming, so hold the spinner.
  // With updates off there is nothing to wait on but the report.
  if (loading || (updatesEnabled && !hasReport && updatesLoading)) {
    return (
      <div className={`homepage-branch-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
        <div className="branch-header">
          <div className="branch-title report-branch-title">
            <Newspaper className="report-icon" />
            <span>{sectionTitle}</span>
          </div>
        </div>
        <div className="report-loading-container">
          <Spinner animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  // No report and no updates to fall back on.
  if (!hasReport && !showUpdatesSection) return null;

  const isViewingHistory = selectedReport !== null;

  const handleCardClick = () => {
    setSelectedReport(null);
    setShowModal(true);
  };

  return (
    <div className={`homepage-branch-carousel ${isFirstCarousel ? 'homepage-first-carousel' : ''}`}>
      {/* Each section carries its own header, so the report and the updates
          read as two feeds rather than one mislabelled list. */}
      {hasReport && (
        <div className="branch-header">
          <div className="branch-title report-branch-title">
            <Newspaper className="report-icon" />
            <span>{reportTitle}</span>
          </div>
        </div>
      )}

        {/* Report Card */}
        {hasReport && currentContent && (
          <div className="report-section-wrapper" ref={reportSectionRef}>
            <div className="feed-item feed-item--homepage mb-0">
              <Card>
                <Card.Title 
                  id="billText" 
                  className="card-title p-3"
                  onClick={handleCardClick}
                  style={{ cursor: 'pointer' }}
                >
                  {generating && (
                    <span className="report-generating-indicator">
                      <Spinner size="sm" animation="border" variant="light" className="report-generating-spinner" />
                    </span>
                  )}
                </Card.Title>
                
                <Card.Title
  id="billText"
  className="card-title report-card-title p-3"
  onClick={handleCardClick}
  style={{ cursor: 'pointer' }}
>
  {getPlainTextPreview(currentContent, 320)}
</Card.Title>
                
              <Card.Footer className="d-flex">
                <Button className="clickable me-2" id="billLink" onClick={handleCardClick}>
                  <FileText /> {t('common:read', 'Read')}
                </Button>
                <CollectionButton item={reportCollectionItem} className="me-2" />
                {reportHistory.length > 1 && (
                  <Button className="clickable" id="addToCollectionButton" onClick={() => setShowHistoryModal(true)}>
                    <ClockHistory />
                  </Button>
                )}
              </Card.Footer>
            </Card>
          </div>

          {currentReport && (
            <div className="report-next-message">
              <small className="report-next-text">
                {generating ? (
                  <>
                    <ArrowClockwise className="spin-animation" size={10} />
                    {t('common:generatingNewReport', 'Generating new report...')}
                  </>
                ) : timeUntilNextReport === 'now' ? (
                  t('common:newReportAvailable', 'New report available')
                ) : (
                  t('common:nextReportIn', 'Next report in {{time}}', { time: timeUntilNextReport || t('common:hours24', '24 hours') })
                )}
              </small>
            </div>
          )}
        </div>
      )}

      {/* Recent pages from official sites in this scope. */}
      {/* {showUpdatesSection && (
        <>
        <div className="branch-header">
          <div className="branch-title report-branch-title">
            <Newspaper className="report-icon" />
            <span>{updatesTitle}</span>
          </div>
        </div>
        <div className="report-section-wrapper">
          {updatesAreEmpty && (
            <p className="report-no-content report-no-content--centered">
              {updatesError
                ? t('common:updatesUnavailable', 'Government website updates are unavailable right now.')
                : t('common:noUpdatesIndexed', 'No indexed government pages for this location yet.')}
            </p>
          )}

          {visibleUpdates.map((item, index) => {
            const url = getUpdateUrl(item);
            const openPage = () => url && openCapacitorSite(url, '_blank');
            return (
              <div key={`update-${url || index}-${index}`} className="feed-item feed-item--default mb-4">
                <Card>
                  <Card.Title
                    id="billText"
                    className="card-title p-3"
                    onClick={openPage}
                    style={{ cursor: url ? 'pointer' : 'default' }}
                  >
                    {truncateText(getUpdateTitle(item), 200)}
                  </Card.Title>
                  <Card.Body id="billBodyText">
                    {formatDate(getUpdateDate(item))}
                  </Card.Body>
                  <Card.Footer className="d-flex">
                    <Button className="clickable me-2" id="billLink" onClick={openPage}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:page', 'Page')}
                    </Button>
                    <CollectionButton item={buildUpdateCollectionItem(item)} />
                  </Card.Footer>
                </Card>
              </div>
            );
          })}

          {!hasReport && (
            <div className="report-next-message">
              <small className="report-next-text">
                {generating ? (
                  <>
                    <ArrowClockwise className="spin-animation" size={10} />
                    {t('common:generatingNewReport', 'Generating new report...')}
                  </>
                ) : (
                  t('common:showingWebCrawlUpdates', 'No report yet. Showing recent web crawl updates.')
                )}
              </small>
            </div>
          )}
        </div>
        </>
      )} */}

      {/* Main Modal */}
      <Modal 
        id="fullBillModal" 
        show={showModal} 
        size="xl" 
        centered 
        onHide={handleCloseModal}
        scrollable
      >
        <Modal.Header id="billModalHeader" closeButton>
          <Modal.Title>
            <Newspaper className="me-2" />
            {isViewingHistory
              ? t('common:pastReport', 'Past {{title}}', { title: reportTitle })
              : reportTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body id="descriptionModalBody" className="report-modal-body">
          {isViewingHistory && (
            <div className="report-back-button-container">
              <Button id="billLink" size="sm" onClick={() => setSelectedReport(null)}>
                {t('common:backToCurrentReport', '\u2190 Back to Current Report')}
              </Button>
            </div>
          )}

          <div className="report-meta-info">
            <small>
              {displayReport?.formattedDate}
              {displayReport?.reportTitle && (
                <span> • <GeoAlt className="me-1" />{displayReport.reportTitle}</span>
              )}
            </small>
          </div>

          {content ? (
            <ReportContent 
              content={content} 
              citations={allNews}
              className="report-modal-content"
            />
          ) : (
            <p className="report-no-content">{t('common:noSummaryForReport', 'No summary available for this report.')}</p>
          )}

          {allNews.length > 0 && (
            <>
              <h5 className="mt-4 mb-3"><List className="me-2" />{t('common:sourcesCount', 'Sources ({{count}})', { count: allNews.length })}</h5>
              {allNews.map((citation, index) => (
                <div key={`modal-${citation.url}-${index}`} className="feed-item mb-3">
                  <Card>
                    <Card.Title 
                      id="billText" 
                      className="card-title p-3"
                      onClick={() => openCapacitorSite(citation.url, '_blank')}
                      style={{ cursor: 'pointer' }}
                    >
                      {truncateText(citation.title, 200)}
                    </Card.Title>
                    <Card.Body id="billBodyText">
                      {citation.date ? formatDate(citation.date) : ''}
                    </Card.Body>
                    <Card.Footer className="d-flex">
                      <Button 
                        className="clickable me-2" 
                        id="billLink" 
                        onClick={() => openCapacitorSite(citation.url, '_blank')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
                      </Button>
                      <CollectionButton item={buildCitationCollectionItem(citation)} />
                    </Card.Footer>
                  </Card>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* History Modal */}
      <Modal 
        id="fullBillModal"
        show={showHistoryModal} 
        size="xl" 
        centered 
        onHide={handleCloseHistoryModal}
        scrollable
      >
        <Modal.Header id="billModalHeader" closeButton>
          <Modal.Title>
            <ClockHistory className="me-2" />
            {t('common:reportHistory', '{{title}} History', { title: reportTitle })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body id="descriptionModalBody" className="report-modal-body">
          {reportHistory.length > 0 ? (
            reportHistory.map((report) => {
              const newsCount = getReportNews(report).length;
              const hasContent = getReportContent(report) !== null;
              return (
                <div 
                  key={report.id} 
                  className="feed-item mb-3 report-history-item"
                  onClick={() => {
                    setSelectedReport(report);
                    setShowHistoryModal(false);
                    setShowModal(true);
                  }}
                >
                  <Card>
                    <Card.Title id="billText" className="card-title p-3">
                      {report.formattedDate}
                    </Card.Title>
                    <Card.Body id="billText" className="card-title p-3">
                      <GeoAlt className="me-1" />{report.reportTitle || t('common:unknown', 'Unknown')} • {t('common:articlesCount', '{{count}} articles', { count: newsCount })}
                      {hasContent && <span> • <FileText className="ms-2 me-1" />{t('common:summaryAvailable', 'Summary available')}</span>}
                    </Card.Body>
                  </Card>
                </div>
              );
            })
          ) : (
            <p className="report-no-content report-no-content--centered">{t('common:noReportHistory', 'No report history available.')}</p>
          )}
        </Modal.Body>
      </Modal>

      {/* Collection Modal */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </div>
  );
};

export default PersonalizedNewsReport;